import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Badge, Button, Form, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getActualDocuments, getTopics, getDocumentTopics } from '@/services/api';
import { TopicBadge } from '@/components/TopicBadge';

interface ActualDoc {
  docid: string;
  docname: string;
  docpassing: string;
  adoption_number: string | null;
  adoption_date: string | null;
  docstate: string | null;
  actual_redid: number | null;
  linked_document_id: string | null;
  linked_eo: string | null;
  linked_title: string | null;
  article_count?: number;
  has_chunks?: boolean;
  topics?: any[];
}

type StatusFilter = '' | 'active' | 'amended' | 'repealed';
type CrmFilter = '' | 'linked' | 'unlinked';

const statusMap: Record<string, { label: string; class: string }> = {
  'Действует без изменений': { label: 'Действует', class: 'badge-active' },
  'Действует с изменениями': { label: 'С изменениями', class: 'badge-amended' },
  'Утратил силу': { label: 'Утратил силу', class: 'badge-repealed' },
  'Не вступил в силу': { label: 'Не вступил', class: 'badge-draft' },
  'Не действует': { label: 'Не действует', class: 'badge-draft' },
  'Отменен': { label: 'Отменен', class: 'badge-draft' },
};

function getStatusInfo(state: string | null) {
  if (!state) return { label: 'Неизвестно', class: 'badge-draft' };
  const normalized = state.trim();
  for (const [key, val] of Object.entries(statusMap)) {
    if (normalized.includes(key)) return val;
  }
  if (normalized.includes('Утратил')) return statusMap['Утратил силу'];
  if (normalized.includes('изменениями')) return statusMap['Действует с изменениями'];
  if (normalized.includes('Действует')) return statusMap['Действует без изменений'];
  if (normalized.includes('Отменен')) return statusMap['Отменен'];
  if (normalized.includes('Не действует')) return statusMap['Не действует'];
  return { label: normalized, class: 'badge-draft' };
}

export const ActualPage: React.FC = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<ActualDoc[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [crmFilter, setCrmFilter] = useState<CrmFilter>('');
  const [topicFilter, setTopicFilter] = useState('');
  const [availableTopics, setAvailableTopics] = useState<any[]>([]);
  const [docTopics, setDocTopics] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<{ id: number; msg: string; type: string }[]>([]);
  const [stats, setStats] = useState({ total: 0, withArticles: 0, linked: 0, chunked: 0 });

  const limit = 20;

  useEffect(() => {
    getTopics().then(r => setAvailableTopics(r.topics || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadDocs();
  }, [page, search, statusFilter, crmFilter, topicFilter]);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDocs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (search) params.search = search;
      
      if (statusFilter === 'active') params.status = encodeURIComponent('Действует');
      if (statusFilter === 'amended') params.status = encodeURIComponent('с изменениями');
      if (statusFilter === 'repealed') params.status = encodeURIComponent('Утратил');
      
      if (crmFilter === 'linked') params.has_linked = 'true';
      if (crmFilter === 'unlinked') params.has_linked = 'false';
      if (topicFilter) params.topic = topicFilter;
      
      const queryString = new URLSearchParams(params).toString();
      const res = await fetch(`/api/actual/documents?${queryString}`).then(r => r.json());
      const items = res.items || [];
      setDocs(items);
      setTotal(res.total || 0);
      setSelectedIds(new Set());

      if (items.length > 0) {
        const entries: Record<string, any[]> = {};
        await Promise.all(items.map(async (doc: ActualDoc) => {
          try {
            const r = await getDocumentTopics(doc.docid);
            entries[doc.docid] = r.topics || [];
          } catch { entries[doc.docid] = []; }
        }));
        setDocTopics(entries);
      } else {
        setDocTopics({});
      }
    } catch (err: any) {
      setError(err.message);
      showToast(err.message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch('/api/actual/stats').then(r => r.json());
      setStats(res);
    } catch {
      // silently fail
    }
  };

  const showToast = (msg: string, type = 'primary') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      await fetch('/api/actual/sync', { 
        method: 'POST', 
        headers: {'Content-Type': 'application/json'}, 
        body: '{}' 
      });
      showToast('Синхронизация запущена', 'success');
      setTimeout(() => loadDocs(), 2000);
    } catch (err: any) {
      showToast('Ошибка синхронизации: ' + err.message, 'danger');
    } finally {
      setSyncing(false);
    }
  };

  const exportExcel = async () => {
    try {
      const res = await fetch('/api/actual/export', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ ids: Array.from(selectedIds), format: 'excel' })
      });
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `actual_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      showToast('CSV сформирован', 'success');
    } catch (err: any) {
      showToast('Ошибка экспорта: ' + err.message, 'danger');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === docs.length && docs.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(docs.map(d => d.docid)));
    }
  };

  const paginationItems = useMemo(() => {
    const items: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
    } else if (page <= 4) {
      for (let i = 1; i <= 5; i++) items.push(i);
      items.push('...');
      items.push(totalPages);
    } else if (page >= totalPages - 3) {
      items.push(1);
      items.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) items.push(i);
    } else {
      items.push(1);
      items.push('...');
      for (let i = page - 1; i <= page + 1; i++) items.push(i);
      items.push('...');
      items.push(totalPages);
    }
    return items;
  }, [page, totalPages]);

  const formatDate = (d: string | null) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('ru-RU');
    } catch { return d; }
  };

  return (
    <Container fluid className="px-4 mt-4">
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1060 }}>
        {toasts.map(t => (
          <Toast key={t.id} bg={t.type} onClose={() => setToasts(prev => prev.filter(x => x.id !== t.id))} delay={3000} autohide>
            <Toast.Body className="text-white">{t.msg}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>

      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <nav aria-label="breadcrumb" className="mb-1">
            <ol className="breadcrumb mb-0" style={{ fontSize: '14px' }}>
              <li className="breadcrumb-item"><Link to="/" className="text-decoration-none">Главная</Link></li>
              <li className="breadcrumb-item active text-muted">Актуальное право</li>
            </ol>
          </nav>
          <h3 className="mb-1 fw-bold">Актуальное право</h3>
          <p className="text-muted mb-0">Документы из <code>actual.pravo.gov.ru</code></p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={runSync} disabled={syncing}>
            <i className={`bi ${syncing ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'} me-2`}></i>
            {syncing ? 'Синхронизация...' : 'Синхронизировать'}
          </Button>
          <Link to="/rag" className="btn btn-primary btn-sm">
            <i className="bi bi-robot me-2"></i>AI Ассистент
          </Link>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <div className="metric-card">
            <div className="metric-icon bg-primary bg-opacity-10 text-primary">
              <i className="bi bi-file-text fs-4"></i>
            </div>
            <div className="metric-value">{stats.total.toLocaleString('ru-RU')}</div>
            <div className="metric-label">Всего редакций</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="metric-card">
            <div className="metric-icon bg-success bg-opacity-10 text-success">
              <i className="bi bi-check-circle fs-4"></i>
            </div>
            <div className="metric-value">{stats.withArticles.toLocaleString('ru-RU')}</div>
            <div className="metric-label">Со статьями</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="metric-card">
            <div className="metric-icon bg-warning bg-opacity-10 text-warning">
              <i className="bi bi-link-45deg fs-4"></i>
            </div>
            <div className="metric-value">{stats.linked.toLocaleString('ru-RU')}</div>
            <div className="metric-label">Связано с CRM</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="metric-card">
            <div className="metric-icon bg-info bg-opacity-10 text-info">
              <i className="bi bi-lightning-charge fs-4"></i>
            </div>
            <div className="metric-value">{stats.chunked.toLocaleString('ru-RU')}</div>
            <div className="metric-label">Векторизовано</div>
          </div>
        </div>
      </div>

      <div className="table-card mb-3">
        <div className="table-card-header">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h5 className="mb-0"><i className="bi bi-bank me-2"></i>Документы</h5>
            <span className="text-muted" style={{ fontSize: '13px' }}>
              {selectedIds.size > 0 && <Badge bg="primary" className="me-2">{selectedIds.size} выбрано</Badge>}
              Показано {docs.length} из {total.toLocaleString('ru-RU')}
            </span>
          </div>
          <div className="d-flex gap-2">
            {selectedIds.size > 0 && (
              <Button variant="outline-success" size="sm" onClick={exportExcel}>
                <i className="bi bi-file-earmark-excel me-2"></i>CSV
              </Button>
            )}
          </div>
        </div>

        <div className="p-3 border-bottom bg-light">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-lg-4">
              <div className="search-box-lg" style={{ maxWidth: '100%' }}>
                <i className="bi bi-search"></i>
                <Form.Control
                  type="text"
                  placeholder="Поиск по названию или номеру закона..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  style={{ paddingLeft: '40px' }}
                />
                {search && (
                  <button
                    className="btn btn-sm btn-link text-decoration-none position-absolute"
                    style={{ right: '8px', top: '50%', transform: 'translateY(-50%)' }}
                    onClick={() => setSearch('')}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-6 col-lg-2">
              <Form.Select size="sm" value={statusFilter} onChange={e => { setStatusFilter(e.target.value as StatusFilter); setPage(1); }}>
                <option value="">Все статусы</option>
                <option value="active">Действует</option>
                <option value="amended">С изменениями</option>
                <option value="repealed">Утратил силу</option>
              </Form.Select>
            </div>
            <div className="col-6 col-lg-2">
              <Form.Select size="sm" value={crmFilter} onChange={e => { setCrmFilter(e.target.value as CrmFilter); setPage(1); }}>
                <option value="">Все документы</option>
                <option value="linked">Связано с CRM</option>
                <option value="unlinked">Не связано</option>
              </Form.Select>
            </div>
            <div className="col-6 col-lg-2">
              <Form.Select size="sm" value={topicFilter} onChange={e => { setTopicFilter(e.target.value); setPage(1); }}>
                <option value="">Все темы</option>
                {availableTopics.filter(t => t.doc_count > 0).map(t => (
                  <option key={t.code} value={t.code}>{t.name} ({t.doc_count})</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-6 col-lg-1">
              <Button variant="outline-secondary" size="sm" className="w-100" onClick={() => { setSearch(''); setStatusFilter(''); setCrmFilter(''); setTopicFilter(''); setPage(1); }}>
                <i className="bi bi-x-lg me-1"></i>Сбросить
              </Button>
            </div>
            <div className="col-6 col-lg-1">
              <Button variant="primary" size="sm" className="w-100" onClick={loadDocs}>
                <i className="bi bi-arrow-clockwise me-1"></i>Обновить
              </Button>
            </div>
          </div>
        </div>

        <div className="d-none d-lg-block">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 40 }} className="ps-3">
                    <input type="checkbox" className="form-check-input" checked={docs.length > 0 && selectedIds.size === docs.length} onChange={selectAll} />
                  </th>
                  <th style={{ width: 60, fontSize: "12px" }} className="text-muted">№</th>
                  <th>Документ</th>
                  <th style={{ width: 100 }}>Номер</th>
                  <th style={{ width: 140 }}>Статус</th>
                  <th style={{ width: 160 }}>Связь с CRM</th>
                  <th style={{ width: 160 }} className="text-end pe-3">Действия</th>
                </tr>
              </thead>
              <tbody>
                {loading && docs.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-5"><Spinner animation="border" size="sm" /></td></tr>
                ) : docs.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-5 text-muted">Документы не найдены</td></tr>
                ) : (
                  docs.map((doc, i) => {
                    const status = getStatusInfo(doc.docstate);
                    const isSelected = selectedIds.has(doc.docid);
                    return (
                      <tr key={doc.docid} className={isSelected ? 'table-primary' : ''}>
                        <td className="ps-3">
                          <input type="checkbox" className="form-check-input" checked={isSelected} onChange={() => toggleSelect(doc.docid)} />
                        </td>
                        <td className="text-muted" style={{ fontSize: '13px' }}>{(page - 1) * limit + i + 1}</td>
                        <td>
                          <Link to={`/actual/document/${doc.docid}`} className="text-decoration-none fw-semibold text-dark" style={{ fontSize: '14px', lineHeight: 1.4 }}>
                            {doc.docname}
                          </Link>
                          <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
                            <i className="bi bi-calendar3 me-1"></i>{formatDate(doc.adoption_date)}
                            {doc.article_count !== undefined && doc.article_count > 0 && (
                              <span className="ms-2"><i className="bi bi-file-earmark-text me-1"></i>{doc.article_count} ст.</span>
                            )}
                            {doc.has_chunks && (
                              <span className="ms-2 text-success"><i className="bi bi-lightning-charge me-1"></i>Векторизован</span>
                            )}
                          </div>
                          <div className="d-flex flex-wrap align-items-center mt-1">
                            {(docTopics[doc.docid] || []).slice(0, 3).map((t: any) => (
                              <TopicBadge key={t.code} topic={t} />
                            ))}
                            {(docTopics[doc.docid] || []).length > 3 && (
                              <Badge bg="light" text="muted" className="mb-1" style={{fontSize:'0.75rem'}}>+{(docTopics[doc.docid].length - 3)}</Badge>
                            )}
                            {!(docTopics[doc.docid]?.length) && (
                              <span className="text-muted" style={{fontSize:'12px'}}>—</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {doc.adoption_number ? (
                            <span className="badge bg-light text-dark border" style={{ fontSize: '12px' }}>{doc.adoption_number}</span>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td><span className={`badge-status ${status.class}`}>{status.label}</span></td>
                        <td>
                          {doc.linked_document_id ? (
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: '12px' }}>
                                <i className="bi bi-briefcase me-1"></i>{doc.linked_eo || 'Связан'}
                              </span>
                              <button className="btn btn-link p-0 text-decoration-none" style={{ fontSize: '12px' }} onClick={() => navigate(`/document/${doc.linked_document_id}`)}>
                                <i className="bi bi-box-arrow-up-right"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-muted" style={{ fontSize: '12px' }}><i className="bi bi-link-45deg me-1"></i>Нет связи</span>
                              <button className="btn btn-outline-primary btn-xs py-0 px-2" style={{ fontSize: '11px' }} onClick={() => navigate(`/document/new?link_actual=${doc.docid}`)} title="Связать с CRM">
                                <i className="bi bi-plus-lg"></i>
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="text-end pe-3">
                          <div className="d-inline-flex gap-1">
                            <button className="btn btn-light btn-sm" title="Просмотр" onClick={() => navigate(`/actual/document/${doc.docid}`)}><i className="bi bi-eye text-primary"></i></button>
                            <button className="btn btn-light btn-sm" title="Сравнить редакции" onClick={() => navigate(`/actual/document/${doc.docid}?compare=1`)}><i className="bi bi-arrow-left-right text-info"></i></button>
                            <button className="btn btn-light btn-sm" title="В избранное" onClick={() => showToast('Добавлено в избранное', 'success')}><i className="bi bi-star text-warning"></i></button>
                            <button className="btn btn-light btn-sm" title="AI Ассистент" onClick={() => navigate(`/rag?docid=${doc.docid}&q=${encodeURIComponent(doc.docname)}`)}><i className="bi bi-robot text-success"></i></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="d-lg-none p-3">
          {loading && docs.length === 0 ? (
            <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
          ) : docs.length === 0 ? (
            <div className="text-center py-5 text-muted">Документы не найдены</div>
          ) : (
            docs.map((doc, i) => {
              const status = getStatusInfo(doc.docstate);
              return (
                <div key={doc.docid} className="card mb-3 border-0 shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="form-check">
                        <input type="checkbox" className="form-check-input" checked={selectedIds.has(doc.docid)} onChange={() => toggleSelect(doc.docid)} />
                      </div>
                      <span className="text-muted" style={{ fontSize: '12px' }}>#{(page - 1) * limit + i + 1}</span>
                      <span className={`badge-status ${status.class}`} style={{ fontSize: '11px' }}>{status.label}</span>
                    </div>
                    <h6 className="card-title mb-2" style={{ fontSize: '14px', lineHeight: 1.4 }}>
                      <Link to={`/actual/document/${doc.docid}`} className="text-decoration-none text-dark">{doc.docname}</Link>
                    </h6>
                    <div className="text-muted mb-2" style={{ fontSize: '12px' }}>
                      <i className="bi bi-calendar3 me-1"></i>{formatDate(doc.adoption_date)}
                      {doc.adoption_number && <span className="ms-2 badge bg-light text-dark border" style={{ fontSize: '11px' }}>{doc.adoption_number}</span>}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      {doc.linked_document_id ? (
                        <span className="badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: '12px' }}><i className="bi bi-briefcase me-1"></i>Связан</span>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '12px' }}><i className="bi bi-link-45deg me-1"></i>Нет связи</span>
                      )}
                      {doc.has_chunks && <span className="text-success" style={{ fontSize: '12px' }}><i className="bi bi-lightning-charge me-1"></i>Векторизован</span>}
                    </div>
                    <div className="d-flex flex-wrap align-items-center mb-2">
                      {(docTopics[doc.docid] || []).slice(0, 2).map((t: any) => (
                        <TopicBadge key={t.code} topic={t} />
                      ))}
                      {(docTopics[doc.docid] || []).length > 2 && (
                        <Badge bg="light" text="muted" className="mb-1" style={{fontSize:'0.75rem'}}>+{(docTopics[doc.docid].length - 2)}</Badge>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-outline-primary btn-sm flex-fill" onClick={() => navigate(`/actual/document/${doc.docid}`)}><i className="bi bi-eye"></i> Просмотр</button>
                      <button className="btn btn-outline-success btn-sm" onClick={() => navigate(`/rag?docid=${doc.docid}`)}><i className="bi bi-robot"></i></button>
                      <button className="btn btn-outline-warning btn-sm" onClick={() => showToast('В избранное', 'success')}><i className="bi bi-star"></i></button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top">
            <div className="text-muted" style={{ fontSize: '13px' }}>Страница {page} из {totalPages}</div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}><i className="bi bi-chevron-left"></i></button>
                </li>
                {paginationItems.map((item, idx) => (
                  <li key={idx} className={`page-item ${item === page ? 'active' : ''} ${item === '...' ? 'disabled' : ''}`}>
                    {item === '...' ? <span className="page-link">...</span> : <button className="page-link" onClick={() => typeof item === 'number' && setPage(item)}>{item}</button>}
                  </li>
                ))}
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}><i className="bi bi-chevron-right"></i></button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </Container>
  );
};
